import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FiSave, FiCpu, FiPlus, FiTrash2, FiArrowLeft, FiServer, FiCloud, FiLock, FiUpload, FiKey } from "react-icons/fi";
import { MdRouter, MdSettingsInputAntenna } from "react-icons/md";
import fetchDataFromApi from "../../api/IotmateRestApi";
import { putDataToApi } from "../../api/IotmateRestApi";
import { IotmateInput, IotmateButton, IotmateToast, AddDeviceDialogBox } from "../../components/Shared";
import { Table } from "../../components/Table";
import { dummyIrrigationLoraGateways, dummyDeviceList } from "../../Data/Data";
import "./GwSetting.css";

const GWSetting = () => {
  const { gatewayId } = useParams();
  const navigate = useNavigate();

  const [gatewayName, setGatewayName] = useState("");
  const [gatewayIdState, setGatewayIdState] = useState("");
  const [loraFrequency, setLoraFrequency] = useState("");
  const [mqttServer, setMqttServer] = useState("");
  const [cloudServer, setCloudServer] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [attachedDevices, setAttachedDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newDevice, setNewDevice] = useState({
    device_id: "",
    name: "",
    type: ""
  });

  const showToast = IotmateToast();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // If gatewayId is provided, try to get data from irrigation gateways or device list
        if (gatewayId) {
          // Check irrigation gateways first
          const irrigationGateway = dummyIrrigationLoraGateways.find(gw => gw.gatewayId === gatewayId);
          if (irrigationGateway) {
            setGatewayName(irrigationGateway.name || `LoRa Gateway ${gatewayId}`);
            setGatewayIdState(irrigationGateway.gatewayId);
            setLoraFrequency(irrigationGateway.frequency?.toString().replace(" MHz", "") || "915");
            // Get control units as attached devices
            if (irrigationGateway.controlUnits) {
              const devices = irrigationGateway.controlUnits.map(unit => ({
                device_id: unit.deviceId,
                name: unit.name,
                type: "Irrigation",
                status: unit.status
              }));
              setAttachedDevices(devices);
            }
          } else {
            // Check device list
            const deviceListGateway = dummyDeviceList.find(d => d.oms_id === gatewayId && d.type === "LoRa Gateway");
            if (deviceListGateway) {
              setGatewayName(deviceListGateway.name || `LoRa Gateway ${gatewayId}`);
              setGatewayIdState(deviceListGateway.oms_id);
              setLoraFrequency("915");
              // Get child devices
              if (deviceListGateway.childDevices) {
                const devices = deviceListGateway.childDevices.map(child => ({
                  device_id: child.oms_id,
                  name: child.name,
                  type: child.type,
                  status: child.status
                }));
                setAttachedDevices(devices);
              }
            }
          }

          // Set default server values
          setMqttServer("mqtt.iotmate.io");
          setCloudServer("cloud.iotmate.io");
        } else {
          // Fetch gateway details from API
          const gwData = await fetchDataFromApi("gw_details");
          if (gwData && gwData.length > 0) {
            setGatewayName(gwData[0].name || "");
            setGatewayIdState(gwData[0].gw_id || "");
            setLoraFrequency(gwData[0].lora_frequency || "915");
          }

          // Fetch attached devices
          const devicesData = await fetchDataFromApi("gw_attached_devices");
          setAttachedDevices(devicesData || []);

          // Set default server values
          setMqttServer("mqtt.iotmate.io");
          setCloudServer("cloud.iotmate.io");
        }
      } catch (error) {
        console.error("Error fetching gateway settings:", error);
        // Set default values on error
        setGatewayName("LoRa Gateway");
        setGatewayIdState(gatewayId || "GW-001");
        setLoraFrequency("915");
        setMqttServer("mqtt.iotmate.io");
        setCloudServer("cloud.iotmate.io");
        // Use dummy data for devices
        setAttachedDevices([
          { device_id: "sensor-node-01", name: "Water Level Sensor 1", type: "Water Level" },
          { device_id: "sensor-node-02", name: "Air Quality Monitor 1", type: "AQM" },
          { device_id: "sensor-node-03", name: "Treated Water Sensor 1", type: "Treated Water" },
        ]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [gatewayId]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleSave = async () => {
    try {
      const data = {
        name: gatewayName,
        gw_id: gatewayIdState,
        lora_frequency: parseInt(loraFrequency),
        mqtt_server: mqttServer,
        cloud_server: cloudServer,
      };

      const resp = await putDataToApi("gw_details/update", data);
      if (resp.status === 200) {
        showToast("Gateway settings updated successfully");
      }
    } catch (error) {
      console.error("Error updating gateway settings:", error);
      showToast("Settings saved locally");
    }
  };

  const handlePasswordChange = () => {
    if (!currentPassword) {
      showToast("Please enter current password");
      return;
    }
    if (newPassword !== confirmPassword) {
      showToast("New passwords do not match");
      return;
    }
    if (newPassword.length < 6) {
      showToast("Password must be at least 6 characters");
      return;
    }
    // In real app, would send to API
    showToast("Password updated successfully");
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
  };

  const handleCertificateUpload = () => {
    // In real app, would open file picker and upload
    showToast("Certificate upload feature - coming soon");
  };

  const handleAddDevice = () => {
    setNewDevice({ device_id: "", name: "", type: "" });
    setIsAddDialogOpen(true);
  };

  const handleCloseAddDialog = () => {
    setIsAddDialogOpen(false);
    setNewDevice({ device_id: "", name: "", type: "" });
  };

  const handleSaveDevice = () => {
    if (newDevice.device_id.trim() && newDevice.name.trim() && newDevice.type) {
      const deviceData = {
        device_id: newDevice.device_id.trim(),
        name: newDevice.name.trim(),
        type: newDevice.type,
      };
      setAttachedDevices([...attachedDevices, deviceData]);
      showToast("Device added successfully");
      setIsAddDialogOpen(false);
      setNewDevice({ device_id: "", name: "", type: "" });
    }
  };

  const handleDeleteDevice = React.useCallback((deviceId) => {
    setAttachedDevices(prevDevices => prevDevices.filter(device => device.device_id !== deviceId));
    showToast("Device removed successfully");
  }, [showToast]);

  const deviceColumns = React.useMemo(
    () => [
      {
        Header: "Device ID",
        accessor: "device_id",
        Cell: ({ value }) => (
          <span style={{
            color: '#3b82f6',
            fontWeight: '600'
          }}>
            {value}
          </span>
        ),
      },
      {
        Header: "Device Name",
        accessor: "name",
      },
      {
        Header: "Type",
        accessor: "type",
        Cell: ({ value }) => (
          <span style={{ fontWeight: '500', color: '#374151' }}>
            {value}
          </span>
        ),
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: ({ value }) => (
          <span style={{
            color: value === "Online" ? '#10b981' : '#ef4444',
            fontWeight: '600'
          }}>
            {value || "N/A"}
          </span>
        ),
      },
      {
        Header: "Actions",
        accessor: "actions",
        Cell: ({ row }) => (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteDevice(row.original.device_id);
              }}
              style={{
                background: '#ef4444',
                color: 'white',
                border: 'none',
                padding: '0.5rem 1rem',
                borderRadius: '0.5rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: '0.875rem',
                fontWeight: '600',
                transition: 'background 0.3s ease'
              }}
              onMouseOver={(e) => e.currentTarget.style.background = '#dc2626'}
              onMouseOut={(e) => e.currentTarget.style.background = '#ef4444'}
            >
              <FiTrash2 size={16} />
              Delete
            </button>
          </div>
        ),
      },
    ],
    [handleDeleteDevice]
  );

  if (loading) {
    return (
      <div className="gw-settings-loading">
        <div className="spinner"></div>
        <p>Loading gateway settings...</p>
      </div>
    );
  }

  return (
    <div className="gw-settings-container">
      {/* Header */}
      <div className="gw-settings-header">
        {gatewayId && (
          <button onClick={handleBack} className="gw-settings-back-btn">
            <FiArrowLeft size={20} />
            Back
          </button>
        )}
        <h1 className="gw-settings-title">Gateway Settings</h1>
        <p className="gw-settings-subtitle">
          Configure your LoRa gateway parameters and manage attached devices
        </p>
      </div>

      {/* Gateway Configuration */}
      <div className="gw-settings-form">
        <h2 className="gw-settings-section-title">Gateway Configuration</h2>

        <div className="gw-settings-grid">
          {/* Gateway Name */}
          <div className="gw-settings-field">
            <label className="gw-settings-label">
              <MdRouter size={20} />
              Gateway Name
            </label>
            <IotmateInput
              type="text"
              placeholder="Enter gateway name"
              value={gatewayName}
              onChange={(e) => setGatewayName(e.target.value)}
              width="100%"
            />
          </div>

          {/* Gateway ID */}
          <div className="gw-settings-field">
            <label className="gw-settings-label">
              <FiCpu size={20} />
              Gateway ID
            </label>
            <IotmateInput
              type="text"
              placeholder="Enter gateway ID"
              value={gatewayIdState}
              onChange={(e) => setGatewayIdState(e.target.value)}
              width="100%"
            />
          </div>

          {/* LoRa Frequency */}
          <div className="gw-settings-field">
            <label className="gw-settings-label">
              <MdSettingsInputAntenna size={20} />
              LoRa Frequency (MHz)
            </label>
            <IotmateInput
              type="number"
              placeholder="Enter frequency (e.g., 915)"
              value={loraFrequency}
              onChange={(e) => setLoraFrequency(e.target.value)}
              width="100%"
            />
          </div>
        </div>

        {/* Save Button */}
        <div className="gw-settings-actions">
          <IotmateButton onClick={handleSave} variant="primary">
            <FiSave size={18} style={{ marginRight: '0.5rem' }} />
            Save Configuration
          </IotmateButton>
        </div>
      </div>

      {/* Server Configuration */}
      <div className="gw-settings-form">
        <h2 className="gw-settings-section-title">Server Configuration</h2>

        <div className="gw-settings-grid">
          {/* MQTT Server */}
          <div className="gw-settings-field">
            <label className="gw-settings-label">
              <FiServer size={20} />
              MQTT Server
            </label>
            <IotmateInput
              type="text"
              placeholder="Enter MQTT server address"
              value={mqttServer}
              onChange={(e) => setMqttServer(e.target.value)}
              width="100%"
            />
            <span className="gw-settings-hint">MQTT broker address for device communication</span>
          </div>

          {/* Cloud Server */}
          <div className="gw-settings-field">
            <label className="gw-settings-label">
              <FiCloud size={20} />
              Cloud Server
            </label>
            <IotmateInput
              type="text"
              placeholder="Enter cloud server address"
              value={cloudServer}
              onChange={(e) => setCloudServer(e.target.value)}
              width="100%"
            />
            <span className="gw-settings-hint">Cloud platform server for data synchronization</span>
          </div>
        </div>

        {/* Save Button */}
        <div className="gw-settings-actions">
          <IotmateButton onClick={handleSave} variant="primary">
            <FiSave size={18} style={{ marginRight: '0.5rem' }} />
            Save Settings
          </IotmateButton>
        </div>
      </div>

      {/* Security Settings */}
      <div className="gw-settings-form">
        <h2 className="gw-settings-section-title">Security Settings</h2>

        {/* Certificate Upload */}
        <div className="gw-settings-security-section">
          <div className="gw-settings-field">
            <label className="gw-settings-label">
              <FiUpload size={20} />
              SSL/TLS Certificate
            </label>
            <div className="gw-settings-certificate">
              <span className="gw-settings-cert-status">Current: certificate.pem (Valid until Dec 2025)</span>
              <IotmateButton onClick={handleCertificateUpload} variant="default">
                <FiUpload size={16} style={{ marginRight: '0.5rem' }} />
                Upload New Certificate
              </IotmateButton>
            </div>
            <span className="gw-settings-hint">Upload SSL/TLS certificate for secure communication</span>
          </div>
        </div>

        {/* Password Change */}
        <div className="gw-settings-security-section">
          <div className="gw-settings-field">
            <label className="gw-settings-label">
              <FiKey size={20} />
              Change Password
            </label>
          </div>

          <div className="gw-settings-password-grid">
            <div className="gw-settings-field">
              <label className="gw-settings-label-small">Current Password</label>
              <IotmateInput
                type="password"
                placeholder="Enter current password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                width="100%"
              />
            </div>

            <div className="gw-settings-field">
              <label className="gw-settings-label-small">New Password</label>
              <IotmateInput
                type="password"
                placeholder="Enter new password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                width="100%"
              />
            </div>

            <div className="gw-settings-field">
              <label className="gw-settings-label-small">Confirm New Password</label>
              <IotmateInput
                type="password"
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                width="100%"
              />
            </div>
          </div>

          <div className="gw-settings-actions" style={{ marginTop: '1rem' }}>
            <IotmateButton onClick={handlePasswordChange} variant="default">
              <FiLock size={16} style={{ marginRight: '0.5rem' }} />
              Update Password
            </IotmateButton>
          </div>
        </div>
      </div>

      {/* Attached Devices List */}
      <div className="gw-settings-devices">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <div>
            <h2 className="gw-settings-section-title" style={{ marginBottom: '0.5rem' }}>Attached Devices</h2>
            <p className="gw-settings-devices-subtitle" style={{ margin: 0 }}>
              {attachedDevices.length} device{attachedDevices.length !== 1 ? 's' : ''} currently attached to this gateway
            </p>
          </div>
          <IotmateButton onClick={handleAddDevice} variant="primary">
            <FiPlus size={18} style={{ marginRight: '0.5rem' }} />
            Add Device
          </IotmateButton>
        </div>

        <div className="gw-settings-table-wrapper">
          <Table
            columns={deviceColumns}
            data={attachedDevices}
            heading="Device List"
            typeComplex={false}
          />
        </div>
      </div>

      {/* Add Device Dialog */}
      <AddDeviceDialogBox
        isOpen={isAddDialogOpen}
        onClose={handleCloseAddDialog}
        onSave={handleSaveDevice}
        device={newDevice}
        onChange={setNewDevice}
        showDeviceId={true}
        showConnectivity={false}
      />
    </div>
  );
};

export default GWSetting;
